<template>
        <date-picker ref="picker"
                     v-model="raw"
                     :lang="{}"
                     :type="type"
                     :clearable="false"
                     :confirm="confirm"
                     :disabled="disabled"
                     :editable="editable"
                     :range="range"
                     :open="open"
                     :time-picker-options="timePickerOptions"
                     :disabled-date="disabledDate"
                     @close="onClose"
                     @click.native="onClicked"
                     @blur="open = false"
                     append-to-body>
            <template #icon-calendar><span></span></template>
            <template #input>
                <b-input-group :size="size" :state="state" style="position:relative">
                    <slot name="prepend" v-if="!!$slots.prepend"></slot>
                    <b-input :id="id" :placeholder="placeholder" :state="state" v-model="formatted" readonly/>
                    <b-input-group-append v-if="clearable || !hideIcon">
                        <b-button-group :size="size">
                            <b-button class="calendar-button" :variant="iconVariant" v-if="!hideIcon">
                                <b-icon-calendar/>
                            </b-button>
                            <b-button class="clear-button" :variant="hasState && !state ? 'danger' : undefined" v-if="clearable">
                                <b-icon-x/>
                            </b-button>
                        </b-button-group>
                    </b-input-group-append>
                    <slot name="append"></slot>
                </b-input-group>
            </template>
        </date-picker>
</template>
<script>

import {Vue, Prop, Component} from 'vue-property-decorator';
import DatePicker from 'vue2-datepicker';
import _ from 'underscore';
import {parseISO, format as dateFormat, isBefore, isAfter} from 'date-fns';

function mkDate(d) {
    switch (true) {
        case !d:
            return null;

        case _.isDate(d):
            return 'Invalid Date' === d.toString() ? null : d;

        case _.isString(d):
            const date = parseISO(d);
            return _.isDate(date) ? date : null;

        default:
            return null;
    }
}

@Component({
    components: {
        DatePicker
    }
})
export default class PickerOfDates extends Vue {
    @Prop({type: String, default: undefined}) id;
    @Prop({type: [Date, Array, String]}) value;
    @Prop({type: String}) type;
    @Prop({type: String, default: 'sm'}) size;
    @Prop({type: Boolean, default: false}) hideIcon;
    @Prop({type: String, default: 'info'}) iconVariant;
    @Prop({type: Boolean, default: undefined}) state;
    @Prop({type: String}) placeholder;
    @Prop({type: Boolean, default: true}) clearable;
    @Prop({type: String, default: 'yyyy-MM-dd'}) format;
    @Prop({type: Boolean, default: false}) range;
    @Prop({type: String, default: ' → '}) rangeSeparator;
    @Prop({type: Boolean, default: false}) confirm;
    @Prop({type: Boolean, default: false}) disabled;
    @Prop({type: Boolean, default: true}) editable;
    @Prop({type: Object, default: () => {
        return { start: '00:00', step: '01:00', end: '23:00' };
    }}) timePickerOptions;

    @Prop({type: Date, default: () => null}) minDate;
    @Prop({type: Date, default: () => null}) maxDate;

    get raw() {
        return this.value;
    }

    set raw(value) {
        this.$emit('input', value);
    }

    get formatted() {
        switch (true) {
            case _.isArray(this.value):
                return _.chain(this.value)
                        .filter(v => !!v)
                        .map(v => dateFormat(v, this.format))
                        .value()
                        .join(this.rangeSeparator);

                case _.isDate(this.value):
                    return dateFormat(this.value, this.format);

                default:
                    const d = mkDate(this.value);
                    return _.isDate(d) ? dateFormat(d, this.format) : '';
        }
    }

    get hasState() {
        return !_.isUndefined(this.state);
    }

    open = false;

    clear() {

        this.open = false;

        switch (true) {
            case this.range:
                this.raw = [null, null];
                break;

            default:
                this.raw = null;
        }
    }

    onClose() {
        this.open = false;
    }

    onClicked(e) {
        const target = e.target;

        if (target.classList.contains('clear-button')) {
            return this.clear();
        }

        const clearButtons = document.querySelectorAll('button.clear-button');
        const calendarButtons = document.querySelectorAll('button.calendar-button');

        if (_.any(clearButtons, (b) => _.any(b.children, (c) => c === target || _.any(c.children, (p) => p === target)))) {
            return this.clear();
        }

        if (_.any(calendarButtons, (b) => _.any(b.children, (c) => c === target || _.any(c.children, (p) => p === target)))) {
            this.open = true;
        }

        if ('INPUT' === target.tagName) {
            this.open = true;
        }
    }

    disabledDate(d) {
        switch (true) {
            case !this.minDate && !this.maxDate:
                return false;

            case !this.minDate:
                return isAfter(d, this.maxDate);

            case !this.maxDate:
                return isBefore(d, this.minDate);

            default:
                return isBefore(d, this.minDate) || isAfter(d, this.maxDate);
        }
    }
}
</script>
<style scoped xml:lang="scss">
    @import '~vue2-datepicker/index.css';
</style>